import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useData } from '../../contexts/DataContext';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { SmsConsentCheckbox } from './SmsConsentCheckbox';

const EditContactModal = ({ isOpen, onClose, contact, onSuccess }) => {
  const { updateContact } = useData();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [modalHeight, setModalHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const modalRef = useRef(null);
  const headerRef = useRef(null);
  const buttonRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: contact?.name || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    company: contact?.company || '',
    address: contact?.address || '',
    sms_consent: Boolean(contact?.sms_consent),
  });

  // Dynamic height calculation
  useEffect(() => {
    if (isOpen && modalRef.current && headerRef.current && buttonRef.current) {
      const calculateDynamicHeight = () => {
        const viewport = window.innerHeight;
        const headerHeight = headerRef.current.offsetHeight;
        const buttonHeight = buttonRef.current.offsetHeight;
        const padding = 32; // Total padding
        const safeArea = 20; // Safe area buffer
        
        // Available height for scrollable content
        const availableHeight = viewport - headerHeight - buttonHeight - padding - safeArea;
        setContentHeight(Math.max(200, availableHeight));
        
        console.log(`Viewport: ${viewport}px, Available for content: ${availableHeight}px`);
      };

      // Calculate initially
      calculateDynamicHeight();
      
      // Recalculate on resize
      window.addEventListener('resize', calculateDynamicHeight);
      return () => window.removeEventListener('resize', calculateDynamicHeight);
    }
  }, [isOpen]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await updateContact(contact.id, formData);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating contact:', error);
      if (error.response?.status === 403) {
        alert(error.response.data.detail || "Permission denied. Please check your subscription status.");
      } else {
        alert('Failed to update contact. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={t('editContact')}
      size="lg"
    >
      <div ref={modalRef} className="h-full flex flex-col">
        <form onSubmit={handleSubmit} className="h-full flex flex-col">
          {/* Dynamic scrollable form content */}
          <div 
            className="flex-1 overflow-y-auto space-y-3 mb-2" 
            style={{ 
              maxHeight: `${contentHeight}px`,
              minHeight: '150px'
            }}
          >
            <div className="space-y-3">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">{t('name')} *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="mobile-input"
                  required
                  placeholder={t('enterContactName')}
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">{t('email')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="mobile-input"
                  placeholder="contact@example.com"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">{t('phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="mobile-input"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">{t('place')}</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="mobile-input"
                  placeholder={t('placeName')}
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">{t('address')}</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows="3"
                  className="mobile-input resize-none"
                  placeholder={t('enterFullAddress')}
                />
              </div>
              <SmsConsentCheckbox
                checked={formData.sms_consent}
                onChange={(v) => setFormData((prev) => ({ ...prev, sms_consent: v }))}
                testId="edit-contact-sms-consent-checkbox"
              />
            </div>
          </div>

          {/* Fixed buttons with ref for measurement */}
          <div ref={buttonRef} className="flex-shrink-0 border-t pt-3 bg-white">
            <div className="flex space-x-3 pb-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all disabled:opacity-50"
              >
                {loading ? `${t('updating')}...` : t('update')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditContactModal;